import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import AppNavbar from '../components/AppNavbar';
import Header from '../components/Header';
import InventoryImage from '../components/InventoryImage';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import {
  assetSettingsFromAsset,
  buildAssetSettingsPayload,
  specificationRowsFromAsset,
} from '../utils/assetSettings';
import { isAmxAdmin } from '../utils/auth';
import axiosInstance from '../utils/axiosInstance';
import { formatRunDuration, isRunTerminal, runContextLabel } from '../utils/assetRuns';
import { canManageBusinessUnit } from '../utils/businessUnitAccess';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const DOCUMENT_CATEGORIES = [
  'manual',
  'datasheet_specification',
  'standard_operating_procedure',
  'work_instruction',
  'maintenance',
  'calibration_certification',
  'troubleshooting_service_bulletin',
  'safety_compliance',
  'integration_interface_guide',
  'expert_notes_reference',
  'training_material',
  'photo_diagram',
  'other',
];

function formatLabel(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : 'N/A';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (!bytes) {
    return 'N/A';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function statusColor(value) {
  const normalized = String(value || '').toLowerCase();
  if (['online', 'running', 'available', 'normal', 'ready_to_start', 'completed', 'picked_up', 'active', 'connected'].includes(normalized)) {
    return 'success';
  }
  if (['degraded', 'paused', 'busy', 'warning', 'reserved', 'prepare_command_sent', 'start_command_sent', 'printing', 'pickup_pending', 'pending', 'dispatching', 'queued', 'abandoned'].includes(normalized)) {
    return 'warning';
  }
  if (['offline', 'faulted', 'stopped', 'critical', 'unavailable', 'failed', 'failed_to_dispatch', 'cancelled', 'inactive', 'retired', 'disconnected'].includes(normalized)) {
    return 'error';
  }
  return 'default';
}

function isExplorerListed(asset) {
  const properties = asset?.properties && typeof asset.properties === 'object'
    ? asset.properties
    : {};
  return properties.list_on_capabilities_explorer ??
    properties.listed_on_capabilities_explorer ??
    properties.is_listed ??
    Boolean(asset?.global_response);
}

function triggerBrowserDownload(url, filename) {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  if (filename) {
    link.download = filename;
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function KeyValueTable({ rows }) {
  return rows.length ? (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Table size="small">
        <TableBody>
          {rows.map(([key, value]) => (
            <TableRow key={key}>
              <TableCell sx={{ width: { xs: 150, md: 240 }, fontWeight: 700 }}>{formatLabel(key)}</TableCell>
              <TableCell sx={{ wordBreak: 'break-word' }}>{formatValue(value)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  ) : (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="body2" color="text.secondary">No data available.</Typography>
    </Paper>
  );
}

function specificationsToRows(specifications) {
  if (!specifications || typeof specifications !== 'object') {
    return [];
  }
  if (Array.isArray(specifications.fields)) {
    return specifications.fields
      .filter((field) => field && typeof field === 'object')
      .map((field) => [
        field.label || field.key || 'Specification',
        [field.value, field.unit].filter(Boolean).join(' '),
      ]);
  }
  return Object.entries(specifications).map(([key, value]) => [key, value]);
}

function commandFormDefaults(command) {
  const properties = command?.parameters_schema?.properties || {};
  return Object.fromEntries(Object.entries(properties).map(([name, definition]) => {
    if (Object.prototype.hasOwnProperty.call(definition, 'default')) {
      return [name, definition.default];
    }
    return [name, definition.type === 'boolean' ? false : ''];
  }));
}

function validatedCommandParameters(command, values) {
  const schema = command?.parameters_schema || {};
  const properties = schema.properties || {};
  const required = new Set(schema.required || []);
  const result = {};

  Object.entries(properties).forEach(([name, definition]) => {
    const raw = values?.[name];
    const empty = raw === '' || raw === null || raw === undefined;
    if (empty) {
      if (required.has(name)) throw new Error(`${definition.title || formatLabel(name)} is required.`);
      return;
    }

    let value = raw;
    if (definition.type === 'integer') {
      value = Number(raw);
      if (!Number.isInteger(value)) throw new Error(`${definition.title || formatLabel(name)} must be an integer.`);
    } else if (definition.type === 'number') {
      value = Number(raw);
      if (!Number.isFinite(value)) throw new Error(`${definition.title || formatLabel(name)} must be a number.`);
    } else if (definition.type === 'boolean') {
      value = Boolean(raw);
    } else {
      value = String(raw);
    }

    if (typeof value === 'number' && definition.minimum !== undefined && value < definition.minimum) {
      throw new Error(`${definition.title || formatLabel(name)} must be at least ${definition.minimum}.`);
    }
    if (typeof value === 'number' && definition.maximum !== undefined && value > definition.maximum) {
      throw new Error(`${definition.title || formatLabel(name)} must be at most ${definition.maximum}.`);
    }
    if (typeof value === 'string' && definition.minLength !== undefined && value.length < definition.minLength) {
      throw new Error(`${definition.title || formatLabel(name)} is too short.`);
    }
    if (typeof value === 'string' && definition.maxLength !== undefined && value.length > definition.maxLength) {
      throw new Error(`${definition.title || formatLabel(name)} is too long.`);
    }
    if (Array.isArray(definition.enum) && !definition.enum.includes(value)) {
      throw new Error(`${definition.title || formatLabel(name)} is not an accepted value.`);
    }
    result[name] = value;
  });
  return result;
}

const EXECUTION_STEPS = [
  ['inputs', 'Command inputs validated'],
  ['artifacts', 'Artifacts prepared'],
  ['run', 'Run created'],
  ['published', 'Command sent to asset'],
  ['ack', 'Asset acknowledgement'],
];

function executionStepIcon(status) {
  if (status === 'success') return <CheckCircleRoundedIcon color="success" fontSize="small" />;
  if (status === 'error') return <ErrorRoundedIcon color="error" fontSize="small" />;
  if (status === 'warning') return <WarningAmberRoundedIcon color="warning" fontSize="small" />;
  if (status === 'active') return <CircularProgress size={18} />;
  return <HourglassTopRoundedIcon color="disabled" fontSize="small" />;
}

function CommandExecutionDialog({ state, onClose, onReissue, onViewRun }) {
  const waiting = state.steps?.ack === 'active' || state.steps?.published === 'active';
  return (
    <Dialog open={state.open} onClose={waiting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Executing &ldquo;{state.command?.display_name || 'Command'}&rdquo;</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          {EXECUTION_STEPS.map(([key, stepLabel]) => (
            <Stack key={key} direction="row" spacing={1.25} alignItems="center">
              {executionStepIcon(state.steps?.[key])}
              <Typography
                variant="body2"
                color={state.steps?.[key] === 'error' ? 'error.main' : 'text.primary'}
              >
                {stepLabel}{key === 'ack' && state.steps?.ack === 'active' ? '…' : ''}
              </Typography>
            </Stack>
          ))}

          {state.error ? (
            <Alert severity={state.status === 'ack_timeout' ? 'warning' : 'error'}>
              {state.error}
            </Alert>
          ) : null}
          {state.status === 'accepted' ? (
            <Alert severity="success">The Asset accepted responsibility for this command.</Alert>
          ) : null}
          {state.runId ? (
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              Run {state.runId} · Command {state.commandId}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        {state.canReissue ? (
          <Button
            startIcon={state.reissuing ? <CircularProgress size={16} /> : <ReplayRoundedIcon />}
            onClick={onReissue}
            disabled={state.reissuing}
          >
            {state.reissuing ? 'Re-Issuing…' : 'Re-Issue Command'}
          </Button>
        ) : null}
        <Button onClick={onClose} disabled={waiting}>Close</Button>
        {state.runId ? <Button variant="contained" onClick={onViewRun}>View Run</Button> : null}
      </DialogActions>
    </Dialog>
  );
}

function CommandParameterField({ fieldId, name, definition, required, value, onChange }) {
  const label = definition.title || formatLabel(name);
  const helperText = definition.description || ' ';

  if (Array.isArray(definition.enum)) {
    return (
      <FormControl fullWidth size="small" required={required}>
        <InputLabel id={`${fieldId}-label`}>{label}</InputLabel>
        <Select
          labelId={`${fieldId}-label`}
          label={label}
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value)}
        >
          <MenuItem value="" disabled={required}>{required ? 'Select a value' : 'None'}</MenuItem>
          {definition.enum.map((option) => (
            <MenuItem key={String(option)} value={option}>{formatLabel(option)}</MenuItem>
          ))}
        </Select>
        {definition.description ? (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
            {definition.description}
          </Typography>
        ) : null}
      </FormControl>
    );
  }

  if (definition.type === 'boolean') {
    return (
      <Box>
        <FormControlLabel
          control={<Switch checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />}
          label={`${label}${required ? ' *' : ''}`}
        />
        {definition.description ? (
          <Typography variant="caption" color="text.secondary" display="block">
            {definition.description}
          </Typography>
        ) : null}
      </Box>
    );
  }

  const numeric = definition.type === 'integer' || definition.type === 'number';
  return (
    <TextField
      fullWidth
      size="small"
      required={required}
      type={numeric ? 'number' : 'text'}
      label={label}
      value={value ?? ''}
      helperText={helperText}
      inputProps={{
        min: definition.minimum,
        max: definition.maximum,
        step: numeric ? (definition.type === 'integer' ? 1 : 'any') : undefined,
        minLength: definition.minLength,
        maxLength: definition.maxLength,
      }}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function CommandCard({ command, values, artifact, onParameterChange, onArtifactChange, onExecute }) {
  const properties = Object.entries(command?.parameters_schema?.properties || {});
  const requiredParameters = new Set(command?.parameters_schema?.required || []);
  const artifactInput = command?.input_artifacts;
  const acceptedMediaTypes = Array.isArray(artifactInput?.accepted_media_types)
    ? artifactInput.accepted_media_types
    : [];

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{command.display_name}</Typography>
              <Chip size="small" label={formatLabel(command.command_type)} variant="outlined" />
            </Stack>
            <Typography variant="caption" color="text.secondary">Operation: {command.operation}</Typography>
            {command.description ? <Typography variant="body2" sx={{ mt: 1 }}>{command.description}</Typography> : null}
          </Box>
          <Typography variant="caption" color="text.secondary">
            Advertised {formatValue(command.advertised_at)}
          </Typography>
        </Stack>

        {properties.length ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            {properties.map(([name, definition]) => (
              <CommandParameterField
                key={name}
                fieldId={`command-${command.asset_command_id || command.operation}-${name}`}
                name={name}
                definition={definition}
                required={requiredParameters.has(name)}
                value={values?.[name]}
                onChange={(value) => onParameterChange(name, value)}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">This command has no structured parameters.</Typography>
        )}

        {artifactInput ? (
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
            <Stack spacing={1}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Button component="label" variant="outlined" startIcon={<CloudUploadRoundedIcon />}>
                  {artifact ? 'Replace Artifact' : 'Select Artifact'}
                  <input
                    hidden
                    type="file"
                    accept={acceptedMediaTypes.join(',') || undefined}
                    onChange={(event) => {
                      onArtifactChange(event.target.files?.[0] || null);
                      event.target.value = '';
                    }}
                  />
                </Button>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {artifact?.name || `One artifact${artifactInput.required ? ' is required' : ' may be supplied'}.`}
                </Typography>
              </Stack>
              {acceptedMediaTypes.length ? (
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" color="text.secondary">Accepted types:</Typography>
                  {acceptedMediaTypes.map((mediaType) => <Chip key={mediaType} size="small" label={mediaType} />)}
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">All file types are accepted.</Typography>
              )}
            </Stack>
          </Paper>
        ) : null}

        <Stack direction="row" justifyContent="flex-end">
          <Tooltip title="Validate and send this command to the Asset.">
            <Button variant="contained" startIcon={<PlayArrowRoundedIcon />} onClick={onExecute}>
              Execute
            </Button>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function AssetManagementPage(props) {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const user = useSelector((reduxState) => reduxState.auth.user);
  const partnerProfile = useSelector((reduxState) => reduxState.auth.partnerProfile);
  const [tab, setTab] = useState('overview');
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [actionState, setActionState] = useState({ busy: '', error: '', success: '' });
  const [artifactForm, setArtifactForm] = useState({ filename: '', content_type: 'application/octet-stream', size_bytes: '' });
  const [telemetryFieldEdits, setTelemetryFieldEdits] = useState({});
  const [availableCapabilities, setAvailableCapabilities] = useState([]);
  const [selectedCapabilityId, setSelectedCapabilityId] = useState('');
  const [commandsState, setCommandsState] = useState({ loading: false, loaded: false, error: '', commands: [] });
  const [commandValues, setCommandValues] = useState({});
  const [commandArtifacts, setCommandArtifacts] = useState({});
  const [executionState, setExecutionState] = useState({
    open: false,
    command: null,
    runId: '',
    commandId: '',
    status: 'idle',
    steps: { inputs: 'pending', artifacts: 'pending', run: 'pending', published: 'pending', ack: 'pending' },
    error: '',
    canReissue: false,
    reissuing: false,
  });
  const [explorerListed, setExplorerListed] = useState(false);
  const [settingsForm, setSettingsForm] = useState(() => assetSettingsFromAsset(null));
  const [settingsSpecificationRows, setSettingsSpecificationRows] = useState(() => specificationRowsFromAsset(null));
  const [settingsOptions, setSettingsOptions] = useState({ loading: false, assetTypes: [] });
  const [settingsImageUploading, setSettingsImageUploading] = useState(false);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [resetExecutionOpen, setResetExecutionOpen] = useState(false);
  const [reservationReason, setReservationReason] = useState('');
  const [documentFile, setDocumentFile] = useState(null);
  const [documentForm, setDocumentForm] = useState({
    title: '',
    category: 'manual',
    category_other: '',
    description: '',
    document_version: '',
    source_author: '',
    document_date: '',
    tags: '',
  });

  const loadAsset = React.useCallback(async (options = {}) => {
    const background = Boolean(options.background);
    if (!background) {
      setState({ loading: true, error: '', data: null });
    }
    try {
      const response = await axiosInstance.get(`/assets/${assetId}`);
      setState({ loading: false, error: '', data: response.data });
    } catch (error) {
      setState((current) => ({
        loading: false,
        error: error?.response?.data?.message || 'Unable to load asset details.',
        data: background ? current.data : null,
      }));
    }
  }, [assetId]);

  const loadCommands = React.useCallback(async () => {
    setCommandsState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await axiosInstance.get(`/assets/${assetId}/commands`);
      const commands = Array.isArray(response.data?.commands) ? response.data.commands : [];
      setCommandsState({ loading: false, loaded: true, error: '', commands });
      setCommandValues((current) => Object.fromEntries(commands.map((command) => {
        const key = command.asset_command_id || command.operation;
        return [key, current[key] || commandFormDefaults(command)];
      })));
      setCommandArtifacts((current) => Object.fromEntries(commands
        .map((command) => command.asset_command_id || command.operation)
        .filter((key) => current[key])
        .map((key) => [key, current[key]])));
    } catch (error) {
      setCommandsState((current) => ({
        ...current,
        loading: false,
        loaded: true,
        error: error?.response?.data?.message || 'Unable to load advertised commands.',
      }));
    }
  }, [assetId]);

  useEffect(() => {
    loadAsset();
  }, [loadAsset]);

  useEffect(() => {
    setCommandsState({ loading: false, loaded: false, error: '', commands: [] });
    setCommandValues({});
    setCommandArtifacts({});
    setExecutionState((current) => ({ ...current, open: false }));
  }, [assetId]);

  useEffect(() => {
    if (tab === 'commands' && !commandsState.loaded && !commandsState.loading) {
      loadCommands();
    }
  }, [commandsState.loaded, commandsState.loading, loadCommands, tab]);

  const asset = state.data?.asset;
  const canManageAsset = useMemo(() => (
    Boolean(asset) && (
      isAmxAdmin(user)
      || canManageBusinessUnit(partnerProfile, asset.business_unit_id, asset.partner_id)
    )
  ), [asset, partnerProfile, user]);

  useEffect(() => {
    setExplorerListed(isExplorerListed(asset));
    setSettingsForm(assetSettingsFromAsset(asset));
    setSettingsSpecificationRows(specificationRowsFromAsset(asset?.specifications));
  }, [asset]);

  useEffect(() => {
    let active = true;
    setSettingsOptions((current) => ({ ...current, loading: true }));
    axiosInstance.get('/asset-types')
      .then((response) => {
        if (!active) return;
        setSettingsOptions({
          loading: false,
          assetTypes: Array.isArray(response.data?.asset_types) ? response.data.asset_types : [],
        });
      })
      .catch(() => {
        if (!active) return;
        setSettingsOptions({ loading: false, assetTypes: [] });
      });

    return () => {
      active = false;
    };
  }, []);

  const latestTelemetry = state.data?.latest_telemetry || {};
  const telemetrySubscriptions = Array.isArray(state.data?.telemetry_subscriptions) ? state.data.telemetry_subscriptions : [];
  const runs = Array.isArray(state.data?.runs) ? state.data.runs : [];
  const activeReservation = state.data?.reservation || null;
  const reservationRun = activeReservation?.run_id
    ? runs.find((run) => run.run_id === activeReservation.run_id)
    : null;
  const artifacts = Array.isArray(state.data?.artifacts) ? state.data.artifacts : [];
  const documents = Array.isArray(state.data?.documents) ? state.data.documents : [];
  const linkedCapabilities = Array.isArray(state.data?.capabilities) ? state.data.capabilities : [];
  const linkedCapabilityIds = useMemo(
    () => new Set(linkedCapabilities.map((capability) => capability.capability_definition_id)),
    [linkedCapabilities],
  );

  useEffect(() => {
    let active = true;
    async function loadCapabilities() {
      if (!asset?.business_unit_id) {
        setAvailableCapabilities([]);
        return;
      }
      try {
        const response = await axiosInstance.get('/partners/user/capabilities', {
          params: { business_unit_id: asset.business_unit_id },
        });
        if (!active) {
          return;
        }
        setAvailableCapabilities(Array.isArray(response.data?.capabilities) ? response.data.capabilities : []);
      } catch (_error) {
        if (active) {
          setAvailableCapabilities([]);
        }
      }
    }
    loadCapabilities();
    return () => {
      active = false;
    };
  }, [asset?.business_unit_id]);

  const overviewRows = useMemo(() => {
    if (!asset) {
      return [];
    }
    return [
      ['Asset Code', asset.asset_code || asset.source_capability_asset_id],
      ['Business Unit', asset.business_unit_display_name || asset.business_unit_code],
      ['Type', asset.asset_type_display_name || formatLabel(asset.asset_type)],
      ['Nature', asset.asset_nature],
      ['Lifecycle', asset.administrative_status],
      ['Manufacturer', asset.manufacturer],
      ['Model', asset.model],
      ['Serial Number', asset.serial_number],
      ['Location', asset.location_label],
      ['Facility', asset.facility_id],
      ['Explorer Listing', explorerListed ? 'Listed' : 'Not listed'],
      ['Created', asset.created_at],
      ['Updated', asset.updated_at],
    ];
  }, [asset, explorerListed]);
  const specificationRows = useMemo(() => specificationsToRows(asset?.specifications), [asset]);
  const telemetryRows = useMemo(() => Object.entries(latestTelemetry?.fields || {}), [latestTelemetry]);
  const availableUnlinkedCapabilities = availableCapabilities.filter((capability) => !linkedCapabilityIds.has(capability.capability_definition_id));
  const settingsAssetTypes = useMemo(() => {
    const options = [...settingsOptions.assetTypes];
    if (settingsForm.asset_type && !options.some((type) => type.asset_type_code === settingsForm.asset_type)) {
      options.push({
        asset_type_code: settingsForm.asset_type,
        display_name: asset?.asset_type_display_name || formatLabel(settingsForm.asset_type),
      });
    }
    return options;
  }, [asset?.asset_type_display_name, settingsForm.asset_type, settingsOptions.assetTypes]);
  const assetSettingsDisabled = !canManageAsset || Boolean(actionState.busy) || settingsImageUploading;

  const setBusy = (busy) => setActionState({ busy, error: '', success: '' });
  const setActionError = (message) => setActionState({ busy: '', error: message, success: '' });
  const setActionSuccess = (message) => setActionState({ busy: '', error: '', success: message });

  const handleDeleteAsset = async () => {
    if (!asset || !window.confirm(`Delete asset "${asset.asset_name || 'Asset'}"? This cannot be undone.`)) {
      return;
    }
    setBusy('delete-asset');
    try {
      await axiosInstance.delete(
        `/partners/${encodeURIComponent(asset.partner_id || asset.business_unit_id)}/assets/${encodeURIComponent(assetId)}`,
      );
      const query = asset.business_unit_id
        ? `?business_unit_id=${encodeURIComponent(asset.business_unit_id)}`
        : '';
      navigate(`/assets${query}`, { replace: true });
    } catch (error) {
      setActionError(error?.response?.data?.message || error?.message || 'Unable to delete asset.');
    }
  };

  const handleExplorerListingChange = async (listed) => {
    const previousValue = explorerListed;
    setExplorerListed(listed);
    setSettingsForm((current) => ({ ...current, list_on_capabilities_explorer: listed }));
    setBusy('explorer-listing');
    try {
      const response = await axiosInstance.patch(`/assets/${assetId}`, {
        global_response: listed,
        properties: {
          list_on_capabilities_explorer: listed,
        },
      });
      setState((current) => ({
        ...current,
        data: current.data ? { ...current.data, asset: response.data } : current.data,
      }));
      setActionSuccess(listed ? 'Asset is now listed on Explorer.' : 'Asset was removed from Explorer.');
    } catch (error) {
      setExplorerListed(previousValue);
      setSettingsForm((current) => ({ ...current, list_on_capabilities_explorer: previousValue }));
      setActionError(error?.response?.data?.message || 'Unable to update the Explorer listing.');
    }
  };

  const updateSettingsForm = (patch) => {
    setSettingsForm((current) => ({ ...current, ...patch }));
  };

  const resetSettingsForm = () => {
    setSettingsForm(assetSettingsFromAsset(asset));
    setSettingsSpecificationRows(specificationRowsFromAsset(asset?.specifications));
    setActionState((current) => ({ ...current, error: '', success: '' }));
  };

  const updateSettingsSpecificationRow = (index, patch) => {
    setSettingsSpecificationRows((current) => current.map((row, rowIndex) => (
      rowIndex === index ? { ...row, ...patch } : row
    )));
  };

  const removeSettingsSpecificationRow = (index) => {
    setSettingsSpecificationRows((current) => {
      const next = current.filter((_row, rowIndex) => rowIndex !== index);
      return next.length ? next : [{ key: '', label: '', value: '', unit: '' }];
    });
  };

  const handleSettingsImageUpload = async (file) => {
    if (!file || !asset?.business_unit_id || !canManageAsset) {
      return;
    }
    setSettingsImageUploading(true);
    setActionState((current) => ({ ...current, error: '', success: '' }));
    try {
      const uploadResponse = await axiosInstance.post('/capability-media/upload', {
        partner_id: asset.business_unit_id,
        filename: file.name,
        content_type: file.type || 'image/png',
        size_bytes: file.size,
        target_kind: 'asset',
      });
      const { url, fields, file_id: fileId } = uploadResponse?.data || {};
      if (!url || !fileId) {
        throw new Error('Image upload did not return a target URL.');
      }
      const uploadForm = new FormData();
      Object.entries(fields || {}).forEach(([key, value]) => uploadForm.append(key, value));
      uploadForm.append('file', file);
      const uploadResult = await fetch(url, { method: 'POST', body: uploadForm });
      if (!uploadResult.ok) {
        throw new Error('Image upload failed.');
      }
      updateSettingsForm({ asset_image_id: fileId });
      setActionState((current) => ({ ...current, success: 'Image uploaded. Save settings to apply it.' }));
    } catch (error) {
      setActionError(error?.response?.data?.message || error?.message || 'Unable to upload image.');
    } finally {
      setSettingsImageUploading(false);
    }
  };

  const handleSettingsSave = async () => {
    if (!canManageAsset) {
      setActionError('You do not have permission to update this asset.');
      return;
    }

    let payload;
    try {
      payload = buildAssetSettingsPayload(settingsForm, settingsSpecificationRows, asset?.specifications);
    } catch (error) {
      setActionError(error.message || 'Review the asset settings and try again.');
      return;
    }

    setBusy('asset-settings');
    try {
      const response = await axiosInstance.patch(`/assets/${assetId}`, payload);
      setState((current) => ({
        ...current,
        data: current.data ? { ...current.data, asset: response.data } : current.data,
      }));
      setActionSuccess('Asset settings updated.');
    } catch (error) {
      setActionError(error?.response?.data?.message || error?.message || 'Unable to update asset settings.');
    }
  };

  const handleReserveAsset = async () => {
    const reason = reservationReason.trim();
    if (!reason) {
      setActionError('A reservation reason is required.');
      return;
    }
    setBusy('reserve-asset');
    try {
      await axiosInstance.post(`/assets/${assetId}/reservations`, { reason });
      setReservationReason('');
      setActionSuccess('Asset reserved.');
      await loadAsset({ background: true });
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to reserve this asset.');
    }
  };

  const handleReleaseReservation = async () => {
    if (!activeReservation?.reservation_id || activeReservation?.reservation_type !== 'manual') {
      return;
    }
    setBusy('release-reservation');
    try {
      await axiosInstance.post(
        `/assets/${assetId}/reservations/${activeReservation.reservation_id}/release`,
      );
      setActionSuccess('Reservation released.');
      await loadAsset({ background: true });
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to release this reservation.');
    }
  };

  const handleResetExecutionState = async () => {
    if (!activeReservation?.run_id || activeReservation?.reservation_type !== 'run') {
      return;
    }
    setBusy('reset-execution');
    try {
      const response = await axiosInstance.post(`/assets/${assetId}/execution/reset`, {
        run_id: activeReservation.run_id,
      });
      setResetExecutionOpen(false);
      setActionSuccess(
        response.data?.idempotent
          ? 'Execution state was already reset.'
          : 'Execution state reset. The reservation was released and linked fulfillment now requires an explicit provider retry.',
      );
      await loadAsset({ background: true });
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to reset this execution state.');
    }
  };

  const handleArtifactUploadRequest = async () => {
    if (!artifactForm.filename.trim()) {
      setActionError('Filename is required.');
      return;
    }
    setBusy('artifact-upload');
    try {
      const response = await axiosInstance.post(`/assets/${assetId}/files/upload-request`, {
        filename: artifactForm.filename.trim(),
        content_type: artifactForm.content_type.trim() || 'application/octet-stream',
        size_bytes: artifactForm.size_bytes ? Number(artifactForm.size_bytes) : undefined,
      });
      setArtifactForm({ filename: '', content_type: 'application/octet-stream', size_bytes: '' });
      setActionSuccess(`Upload URL created for ${response.data.filename || artifactForm.filename}.`);
      await loadAsset({ background: true });
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to create artifact upload URL.');
    }
  };

  const handleDownloadArtifact = async (file) => {
    setBusy(`artifact-download-${file.file_id}`);
    try {
      const response = await axiosInstance.post(`/assets/${assetId}/files/${file.file_id}/download`);
      triggerBrowserDownload(response.data.url, file.file_name || file.filename);
      setActionSuccess(`Started download for ${file.file_name || file.filename}.`);
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to download artifact.');
    }
  };

  const handleDocumentUpload = async () => {
    if (!documentFile) {
      setActionError('Select a document file.');
      return;
    }
    if (documentForm.category === 'other' && !documentForm.category_other.trim()) {
      setActionError('Other category is required.');
      return;
    }
    setBusy('document-upload');
    try {
      const response = await axiosInstance.post(`/assets/${assetId}/documents`, {
        filename: documentFile.name,
        content_type: documentFile.type || 'application/octet-stream',
        size_bytes: documentFile.size,
        title: documentForm.title.trim() || documentFile.name,
        category: documentForm.category,
        category_other: documentForm.category_other.trim() || undefined,
        description: documentForm.description.trim() || undefined,
        document_version: documentForm.document_version.trim() || undefined,
        source_author: documentForm.source_author.trim() || undefined,
        document_date: documentForm.document_date || undefined,
        tags: documentForm.tags,
      });
      const { upload_url: uploadUrl } = response.data || {};
      if (!uploadUrl) {
        throw new Error('Document upload did not return a target URL.');
      }
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': documentFile.type || 'application/octet-stream' },
        body: documentFile,
      });
      if (!uploadResponse.ok) {
        throw new Error('Document upload failed.');
      }
      setDocumentDialogOpen(false);
      setDocumentFile(null);
      setDocumentForm({
        title: '',
        category: 'manual',
        category_other: '',
        description: '',
        document_version: '',
        source_author: '',
        document_date: '',
        tags: '',
      });
      setActionSuccess(`Uploaded ${documentFile.name}.`);
      await loadAsset({ background: true });
    } catch (error) {
      setActionError(error?.response?.data?.message || error?.message || 'Unable to upload document.');
    }
  };

  const handleDocumentDownload = async (document) => {
    setBusy(`document-download-${document.asset_document_id}`);
    try {
      const response = await axiosInstance.post(`/assets/${assetId}/documents/${document.asset_document_id}/download`);
      triggerBrowserDownload(response.data.url, document.file?.file_name || document.title);
      setActionSuccess(`Started download for ${document.title}.`);
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to download document.');
    }
  };

  const handleArchiveDocument = async (document) => {
    setBusy(`document-archive-${document.asset_document_id}`);
    try {
      await axiosInstance.delete(`/assets/${assetId}/documents/${document.asset_document_id}`);
      setActionSuccess(`${document.title} archived.`);
      await loadAsset({ background: true });
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to archive document.');
    }
  };

  const handleAttachCapability = async () => {
    if (!selectedCapabilityId) {
      return;
    }
    setBusy('capability-attach');
    try {
      await axiosInstance.post(`/assets/${assetId}/capabilities/${selectedCapabilityId}`, {
        metadata: { managed_via: 'asset_detail' },
      });
      setSelectedCapabilityId('');
      setActionSuccess('Capability associated.');
      await loadAsset({ background: true });
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to associate capability.');
    }
  };

  const handleDetachCapability = async (capability) => {
    setBusy(`capability-detach-${capability.capability_definition_id}`);
    try {
      await axiosInstance.delete(`/assets/${assetId}/capabilities/${capability.capability_definition_id}`);
      setActionSuccess('Capability association removed.');
      await loadAsset({ background: true });
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to remove capability association.');
    }
  };

  const handleTelemetrySubscriptionUpdate = async (subscription, status) => {
    setBusy(`telemetry-${subscription.telemetry_subscription_id}-${status}`);
    try {
      await axiosInstance.patch(`/telemetry-subscriptions/${subscription.telemetry_subscription_id}`, { status });
      setActionSuccess(`Telemetry subscription ${formatLabel(status).toLowerCase()}.`);
      await loadAsset({ background: true });
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to update telemetry subscription.');
    }
  };

  const telemetryFieldText = (subscription) => {
    if (Object.prototype.hasOwnProperty.call(telemetryFieldEdits, subscription.telemetry_subscription_id)) {
      return telemetryFieldEdits[subscription.telemetry_subscription_id];
    }
    return Array.isArray(subscription.allowed_fields) ? subscription.allowed_fields.join(', ') : '';
  };

  const handleTelemetryFieldsSave = async (subscription) => {
    const allowedFields = telemetryFieldText(subscription)
      .split(',')
      .map((field) => field.trim())
      .filter(Boolean);
    const busyKey = `telemetry-fields-${subscription.telemetry_subscription_id}`;
    setBusy(busyKey);
    try {
      await axiosInstance.patch(`/telemetry-subscriptions/${subscription.telemetry_subscription_id}`, {
        allowed_fields: allowedFields,
      });
      setTelemetryFieldEdits((current) => {
        const next = { ...current };
        delete next[subscription.telemetry_subscription_id];
        return next;
      });
      setActionSuccess('Telemetry field limits saved.');
      await loadAsset({ background: true });
    } catch (error) {
      setActionError(error?.response?.data?.message || 'Unable to save telemetry field limits.');
    }
  };

  const handleCommandParameterChange = (commandKey, parameterName, value) => {
    setCommandValues((current) => ({
      ...current,
      [commandKey]: {
        ...(current[commandKey] || {}),
        [parameterName]: value,
      },
    }));
  };

  const handleCommandArtifactChange = (commandKey, file) => {
    setCommandArtifacts((current) => ({ ...current, [commandKey]: file }));
  };

  const updateExecutionSteps = (steps, extra = {}) => {
    setExecutionState((current) => ({
      ...current,
      ...extra,
      steps: { ...current.steps, ...steps },
    }));
  };

  const waitForCommandAck = React.useCallback(async (runId, timeoutSeconds = 10) => {
    const deadline = Date.now() + ((Number(timeoutSeconds) || 10) + 5) * 1000;
    while (Date.now() <= deadline) {
      try {
        const response = await axiosInstance.get(`/runs/${encodeURIComponent(runId)}`);
        const run = response.data?.run;
        const command = run?.command || {};
        const status = String(command.status || '').toLowerCase();
        if (status === 'accepted' || status === 'acknowledged') {
          setExecutionState((current) => ({
            ...current,
            status: 'accepted',
            error: '',
            canReissue: false,
            steps: { ...current.steps, ack: 'success' },
          }));
          return;
        }
        if (status === 'rejected') {
          setExecutionState((current) => ({
            ...current,
            status: 'rejected',
            error: command.response_payload?.message || command.error_message || 'The Asset rejected this command.',
            canReissue: true,
            steps: { ...current.steps, ack: 'error' },
          }));
          return;
        }
        if (status === 'ack_timeout') {
          setExecutionState((current) => ({
            ...current,
            status: 'ack_timeout',
            error: 'No acknowledgement was received. The Asset may or may not have received the command.',
            canReissue: true,
            steps: { ...current.steps, ack: 'warning' },
          }));
          return;
        }
        if (['failed_to_dispatch', 'validation_failed'].includes(status)) {
          setExecutionState((current) => ({
            ...current,
            status,
            error: command.error_message || 'AMX could not dispatch this command.',
            canReissue: true,
            steps: { ...current.steps, published: 'error', ack: 'pending' },
          }));
          return;
        }
      } catch (error) {
        if (Date.now() > deadline) {
          setExecutionState((current) => ({
            ...current,
            status: 'ack_timeout',
            error: error?.response?.data?.message || 'Unable to confirm whether the Asset acknowledged the command.',
            canReissue: true,
            steps: { ...current.steps, ack: 'warning' },
          }));
          return;
        }
      }
      await new Promise((resolve) => window.setTimeout(resolve, 1000));
    }
    setExecutionState((current) => ({
      ...current,
      status: 'ack_timeout',
      error: 'No acknowledgement was received. The Asset may or may not have received the command.',
      canReissue: true,
      steps: { ...current.steps, ack: 'warning' },
    }));
  }, []);

  const handleCommandExecute = async (command) => {
    const commandKey = command.asset_command_id || command.operation;
    const selectedArtifact = commandArtifacts[commandKey] || null;
    setExecutionState({
      open: true,
      command,
      runId: '',
      commandId: '',
      status: 'validating',
      steps: { inputs: 'active', artifacts: 'pending', run: 'pending', published: 'pending', ack: 'pending' },
      error: '',
      canReissue: false,
      reissuing: false,
    });

    try {
      const parameters = validatedCommandParameters(command, commandValues[commandKey] || {});
      updateExecutionSteps({ inputs: 'success', artifacts: 'active' }, { status: 'preparing_artifacts' });

      const artifactContract = command.input_artifacts;
      if (artifactContract?.required && !selectedArtifact) {
        throw new Error('Select the required input artifact before executing this command.');
      }

      const artifactIds = [];
      if (selectedArtifact) {
        const acceptedTypes = Array.isArray(artifactContract?.accepted_media_types)
          ? artifactContract.accepted_media_types
          : [];
        const contentType = selectedArtifact.type || (acceptedTypes.length === 1 ? acceptedTypes[0] : 'application/octet-stream');
        const uploadResponse = await axiosInstance.post(`/assets/${assetId}/files/upload-request`, {
          filename: selectedArtifact.name,
          content_type: contentType,
          size_bytes: selectedArtifact.size,
          metadata: {
            usage: 'command_input',
            asset_command_id: command.asset_command_id,
            operation: command.operation,
          },
        });
        const uploadUrl = uploadResponse.data?.upload_url;
        const artifactId = uploadResponse.data?.asset_file_id;
        if (!uploadUrl || !artifactId) throw new Error('AMX did not return an artifact upload target.');
        const uploaded = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': contentType },
          body: selectedArtifact,
        });
        if (!uploaded.ok) throw new Error(`Artifact upload failed (${uploaded.status}).`);
        artifactIds.push(artifactId);
      }

      updateExecutionSteps({ artifacts: 'success', run: 'active' }, { status: 'creating_run' });
      const idempotencyKey = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
      const response = await axiosInstance.post(
        `/assets/${assetId}/commands/${encodeURIComponent(command.asset_command_id)}/execute`,
        { parameters, artifact_ids: artifactIds, idempotency_key: idempotencyKey },
      );
      const result = response.data || {};
      const base = {
        runId: result.run_id || '',
        commandId: result.command_id || result.invocation_id || '',
        status: result.status || 'unknown',
      };
      if (result.status === 'validation_failed') {
        updateExecutionSteps(
          { inputs: 'error', run: 'success', published: 'pending', ack: 'pending' },
          { ...base, error: result.error || 'Backend command validation failed.', canReissue: false },
        );
        return;
      }
      if (result.status === 'failed_to_dispatch') {
        updateExecutionSteps(
          { run: 'success', published: 'error', ack: 'pending' },
          { ...base, error: result.error || 'AMX could not publish the command.', canReissue: true },
        );
        return;
      }

      updateExecutionSteps(
        { run: 'success', published: 'success', ack: 'active' },
        { ...base, error: '', canReissue: false },
      );
      await waitForCommandAck(result.run_id, result.ack_timeout_seconds);
    } catch (error) {
      setExecutionState((current) => {
        const artifactActive = current.steps.artifacts === 'active';
        return {
          ...current,
          status: 'failed_to_dispatch',
          error: error?.response?.data?.message || error?.message || 'Unable to execute this command.',
          steps: {
            ...current.steps,
            inputs: current.steps.inputs === 'active' ? 'error' : current.steps.inputs,
            artifacts: artifactActive ? 'error' : current.steps.artifacts,
            run: current.runId ? 'success' : (artifactActive ? 'pending' : 'error'),
          },
        };
      });
    }
  };

  const handleCommandReissue = async () => {
    if (!executionState.runId) return;
    setExecutionState((current) => ({
      ...current,
      reissuing: true,
      canReissue: false,
      error: '',
      status: 'dispatching',
      steps: { ...current.steps, published: 'active', ack: 'pending' },
    }));
    try {
      const response = await axiosInstance.post(
        `/runs/${encodeURIComponent(executionState.runId)}/command/reissue`,
      );
      const result = response.data || {};
      if (result.status === 'failed_to_dispatch' || result.status === 'validation_failed') {
        setExecutionState((current) => ({
          ...current,
          reissuing: false,
          canReissue: true,
          status: result.status,
          error: result.error || 'AMX could not re-issue the command.',
          steps: { ...current.steps, published: 'error' },
        }));
        return;
      }
      setExecutionState((current) => ({
        ...current,
        reissuing: false,
        status: 'awaiting_ack',
        steps: { ...current.steps, published: 'success', ack: 'active' },
      }));
      await waitForCommandAck(executionState.runId, result.ack_timeout_seconds);
    } catch (error) {
      setExecutionState((current) => ({
        ...current,
        reissuing: false,
        canReissue: true,
        status: 'failed_to_dispatch',
        error: error?.response?.data?.message || error?.message || 'Unable to re-issue the command.',
        steps: { ...current.steps, published: 'error' },
      }));
    }
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <CommandExecutionDialog
            state={executionState}
            onClose={() => setExecutionState((current) => ({ ...current, open: false }))}
            onReissue={handleCommandReissue}
            onViewRun={() => navigate(`/runs/${encodeURIComponent(executionState.runId)}`)}
          />
          <Dialog
            open={resetExecutionOpen}
            onClose={actionState.busy === 'reset-execution' ? undefined : () => setResetExecutionOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Reset Execution State?</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 0.5 }}>
                <Typography>
                  This will release the asset from its current run and mark the run as abandoned.
                </Typography>
                <Typography>
                  This only resets AMX&apos;s execution state. It does not send a reset command to the underlying asset.
                </Typography>
                <Typography>
                  Artifacts, outputs, telemetry, or other information received later for the abandoned run will still be retained.
                </Typography>
                <Typography>
                  If this run belongs to automated fulfillment, AMX will wait for the provider to explicitly retry it.
                </Typography>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setResetExecutionOpen(false)}
                disabled={actionState.busy === 'reset-execution'}
              >
                Cancel
              </Button>
              <Button
                color="warning"
                variant="contained"
                onClick={handleResetExecutionState}
                disabled={actionState.busy === 'reset-execution'}
              >
                {actionState.busy === 'reset-execution' ? 'Resetting…' : 'Reset Execution State'}
              </Button>
            </DialogActions>
          </Dialog>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'Assets', href: '/assets' },
              { label: asset?.asset_name || 'Asset' },
            ]}
          />

          {state.loading ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={24} />
              <Typography>Loading asset details...</Typography>
            </Stack>
          ) : state.error ? (
            <Alert severity="error">{state.error}</Alert>
          ) : (
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.default' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ minWidth: 0 }}>
                    <InventoryImage
                      fileId={asset?.asset_image_id}
                      alt={asset?.asset_name || 'Asset image'}
                      height={132}
                      sx={{ width: { xs: '100%', sm: 196 }, flexShrink: 0 }}
                    />
                    <Stack spacing={1} sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Inventory2RoundedIcon color="primary" />
                        <Typography variant="h4" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>
                          {asset?.asset_name || 'Asset'}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {asset?.business_unit_display_name || asset?.business_unit_code || 'Business Unit'}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip label={asset?.asset_type_display_name || formatLabel(asset?.asset_type)} />
                        <Chip label={formatLabel(asset?.administrative_status || 'active')} color={statusColor(asset?.administrative_status)} variant="outlined" />
                        <Chip label={formatLabel(asset?.integration_status || 'not_configured')} color={statusColor(asset?.integration_status)} variant="outlined" />
                      </Stack>
                    </Stack>
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}>
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={explorerListed}
                          onChange={(event) => handleExplorerListingChange(event.target.checked)}
                          disabled={Boolean(actionState.busy) || !canManageAsset}
                        />
                      )}
                      label="List on Explorer"
                    />
                    <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={() => loadAsset()}>
                      Refresh
                    </Button>
                    {canManageAsset ? (
                      <Button
                        color="error"
                        variant="outlined"
                        startIcon={<DeleteRoundedIcon />}
                        onClick={handleDeleteAsset}
                        disabled={Boolean(actionState.busy)}
                      >
                        {actionState.busy === 'delete-asset' ? 'Deleting...' : 'Delete'}
                      </Button>
                    ) : null}
                    <Button variant="contained" startIcon={<ShowChartRoundedIcon />} onClick={() => navigate(`/assets/${assetId}/realtime`)}>
                      Real Time
                    </Button>
                  </Stack>
                </Stack>
              </Paper>

              {actionState.error ? <Alert severity="error">{actionState.error}</Alert> : null}
              {actionState.success ? <Alert severity="success">{actionState.success}</Alert> : null}

              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <Tabs
                  value={tab}
                  onChange={(_event, value) => setTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab value="overview" label="Overview" />
                  <Tab value="live" label="Live Data" />
                  <Tab value="runs" label="Runs" />
                  <Tab value="commands" label="Commands" />
                  <Tab value="artifacts" label="Artifacts" />
                  <Tab value="documents" label="Documents" />
                  <Tab value="capabilities" label="Capabilities" />
                  <Tab value="integration" label="Integration" />
                  <Tab value="settings" label="Settings" />
                </Tabs>
              </Paper>

              {tab === 'overview' ? (
                <Stack spacing={2}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {[
                        ['Connection', asset?.connection_status],
                        ['Operation', asset?.operational_status],
                        ['Availability', state.data?.effective_availability_status || asset?.availability_status],
                        ['Health', asset?.health_status],
                        ['Mode', asset?.mode],
                      ].map(([label, value]) => (
                        <Chip
                          key={label}
                          color={statusColor(value)}
                          label={`${label}: ${value || 'unknown'}`}
                          variant={statusColor(value) === 'default' ? 'outlined' : 'filled'}
                        />
                      ))}
                    </Stack>
                    {asset?.status_message ? (
                      <Typography variant="body2" sx={{ mt: 1.5 }}>{asset.status_message}</Typography>
                    ) : null}
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Asset Reservation</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Active reservations prevent automated fulfillment from selecting this asset.
                          </Typography>
                        </Box>
                        <Chip
                          color={activeReservation ? 'warning' : 'success'}
                          label={activeReservation ? 'Reserved' : 'Available'}
                        />
                      </Stack>
                      {activeReservation ? (
                        <Stack spacing={0.5}>
                          <Typography variant="body2">
                            <strong>Reserved by:</strong>{' '}
                            {activeReservation.reserved_by_user?.full_name
                              || activeReservation.reserved_by_user?.email
                              || formatLabel(activeReservation.reserved_by)}
                          </Typography>
                          <Typography variant="body2"><strong>Reason:</strong> {activeReservation.reason || 'N/A'}</Typography>
                          <Typography variant="body2"><strong>Reserved:</strong> {formatValue(activeReservation.reserved_at)}</Typography>
                          {activeReservation.run_id ? (
                            <Typography variant="body2"><strong>Run:</strong> {activeReservation.run_id}</Typography>
                          ) : null}
                          {activeReservation.request_capability_id ? (
                            <Typography variant="body2">
                              <strong>Requested Capability:</strong> {activeReservation.request_capability_id}
                            </Typography>
                          ) : null}
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
                            {reservationRun?.workspace_id && activeReservation.request_capability_id ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => navigate(
                                  `/workspaces/${encodeURIComponent(reservationRun.workspace_id)}?workstream=${encodeURIComponent(activeReservation.request_capability_id)}`,
                                )}
                              >
                                Open Workstream
                              </Button>
                            ) : null}
                            {activeReservation.reservation_type === 'manual' ? (
                              <Button
                                size="small"
                                color="warning"
                                variant="outlined"
                                onClick={handleReleaseReservation}
                                disabled={actionState.busy === 'release-reservation'}
                              >
                                {actionState.busy === 'release-reservation' ? 'Releasing...' : 'Release Reservation'}
                              </Button>
                            ) : activeReservation.reservation_type === 'run'
                              && activeReservation.run_id
                              && reservationRun
                              && !isRunTerminal(reservationRun) ? (
                              <Button
                                size="small"
                                color="warning"
                                variant="outlined"
                                startIcon={<ReplayRoundedIcon />}
                                onClick={() => setResetExecutionOpen(true)}
                                disabled={actionState.busy === 'reset-execution'}
                              >
                                Reset Execution State
                              </Button>
                            ) : (
                              <Alert severity="info" sx={{ py: 0 }}>
                                This Run reservation no longer has an active execution to reset.
                              </Alert>
                            )}
                          </Stack>
                        </Stack>
                      ) : (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <TextField
                            size="small"
                            label="Reservation reason"
                            value={reservationReason}
                            onChange={(event) => setReservationReason(event.target.value)}
                            sx={{ flex: 1 }}
                          />
                          <Button
                            variant="outlined"
                            onClick={handleReserveAsset}
                            disabled={actionState.busy === 'reserve-asset'}
                          >
                            {actionState.busy === 'reserve-asset' ? 'Reserving...' : 'Reserve Asset'}
                          </Button>
                        </Stack>
                      )}
                    </Stack>
                  </Paper>
                  <KeyValueTable rows={overviewRows} />
                  <KeyValueTable rows={specificationRows} />
                </Stack>
              ) : null}

              {tab === 'live' ? (
                <Stack spacing={2}>
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.default' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Latest Telemetry</Typography>
                      {latestTelemetry?.time ? <Typography variant="caption" color="text.secondary">{latestTelemetry.time}</Typography> : null}
                    </Box>
                    {!latestTelemetry?.configured ? (
                      <Alert severity="info" sx={{ borderRadius: 0 }}>{latestTelemetry?.message || 'Telemetry is not configured.'}</Alert>
                    ) : telemetryRows.length ? (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Field</TableCell>
                            <TableCell>Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {telemetryRows.map(([key, value]) => (
                            <TableRow key={key}>
                              <TableCell sx={{ fontWeight: 700 }}>{formatLabel(key)}</TableCell>
                              <TableCell>{formatValue(value)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Box sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">No telemetry has been ingested for this asset.</Typography></Box>
                    )}
                  </Paper>

                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.default' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Telemetry Sharing</Typography>
                    </Box>
                    {telemetrySubscriptions.length ? (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Thread</TableCell>
                            <TableCell>Requested By</TableCell>
                            <TableCell>Fields</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {telemetrySubscriptions.map((subscription) => (
                            <TableRow key={subscription.telemetry_subscription_id}>
                              <TableCell sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{subscription.corr_id}</TableCell>
                              <TableCell>{subscription.requested_by?.full_name || subscription.requested_by?.email || subscription.requested_by_user_id || 'Requester'}</TableCell>
                              <TableCell>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                  <TextField
                                    size="small"
                                    label="Fields"
                                    placeholder="All fields"
                                    value={telemetryFieldText(subscription)}
                                    onChange={(event) => setTelemetryFieldEdits((current) => ({
                                      ...current,
                                      [subscription.telemetry_subscription_id]: event.target.value,
                                    }))}
                                    sx={{ minWidth: { xs: '100%', sm: 220 } }}
                                  />
                                  <Button
                                    size="small"
                                    startIcon={<SaveRoundedIcon />}
                                    onClick={() => handleTelemetryFieldsSave(subscription)}
                                    disabled={Boolean(actionState.busy)}
                                  >
                                    Save
                                  </Button>
                                </Stack>
                              </TableCell>
                              <TableCell><Chip size="small" color={statusColor(subscription.status)} label={formatLabel(subscription.status)} /></TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={0.75} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                                  {subscription.status !== 'active' && subscription.status !== 'revoked' ? (
                                    <Button size="small" onClick={() => handleTelemetrySubscriptionUpdate(subscription, 'active')} disabled={Boolean(actionState.busy)}>Activate</Button>
                                  ) : null}
                                  {subscription.status === 'active' ? (
                                    <Button size="small" onClick={() => handleTelemetrySubscriptionUpdate(subscription, 'paused')} disabled={Boolean(actionState.busy)}>Pause</Button>
                                  ) : null}
                                  {subscription.status !== 'revoked' ? (
                                    <Button size="small" color="error" onClick={() => handleTelemetrySubscriptionUpdate(subscription, 'revoked')} disabled={Boolean(actionState.busy)}>Revoke</Button>
                                  ) : null}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Box sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">No telemetry subscriptions have been requested for this asset.</Typography></Box>
                    )}
                  </Paper>
                </Stack>
              ) : null}

              {tab === 'runs' ? (
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  {runs.length ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Run</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Progress</TableCell>
                          <TableCell>Started</TableCell>
                          <TableCell>Ended / Duration</TableCell>
                          <TableCell>Shared With</TableCell>
                          <TableCell>Trigger</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {runs.map((run) => (
                          <TableRow
                            key={run.run_id}
                            hover
                            onClick={() => navigate(`/runs/${encodeURIComponent(run.run_id)}`)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{`Run ${String(run.run_id).slice(0, 8)}`}</TableCell>
                            <TableCell><Chip size="small" color={statusColor(run.status)} label={formatLabel(run.status)} /></TableCell>
                            <TableCell>{run.progress === null || typeof run.progress === 'undefined' ? 'N/A' : `${Number(run.progress).toFixed(0)}%`}</TableCell>
                            <TableCell>{formatValue(run.started_at)}</TableCell>
                            <TableCell>{run.ended_at || run.completed_at ? `${formatValue(run.ended_at || run.completed_at)} / ${formatRunDuration(run.duration_seconds)}` : formatRunDuration(run.duration_seconds)}</TableCell>
                            <TableCell>{runContextLabel(run)}</TableCell>
                            <TableCell>{formatLabel(run.trigger_source)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Box sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">No runs have been recorded for this asset.</Typography></Box>
                  )}
                </Paper>
              ) : null}

              {tab === 'commands' ? (
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>Available Commands</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Validate inputs, prepare artifacts, and dispatch connector-advertised commands.
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshRoundedIcon />}
                      onClick={loadCommands}
                      disabled={commandsState.loading}
                    >
                      Refresh Commands
                    </Button>
                  </Stack>

                  {commandsState.loading ? (
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <CircularProgress size={22} />
                      <Typography variant="body2">Loading advertised commands...</Typography>
                    </Stack>
                  ) : null}
                  {commandsState.error ? <Alert severity="error">{commandsState.error}</Alert> : null}
                  {!commandsState.loading && !commandsState.error && commandsState.commands.length ? (
                    commandsState.commands.map((command) => {
                      const commandKey = command.asset_command_id || command.operation;
                      return (
                        <CommandCard
                          key={commandKey}
                          command={command}
                          values={commandValues[commandKey] || commandFormDefaults(command)}
                          artifact={commandArtifacts[commandKey] || null}
                          onParameterChange={(name, value) => handleCommandParameterChange(commandKey, name, value)}
                          onArtifactChange={(file) => handleCommandArtifactChange(commandKey, file)}
                          onExecute={() => handleCommandExecute(command)}
                        />
                      );
                    })
                  ) : null}
                  {commandsState.loaded && !commandsState.loading && !commandsState.error && !commandsState.commands.length ? (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        This asset has not advertised any active commands.
                      </Typography>
                    </Paper>
                  ) : null}
                </Stack>
              ) : null}

              {tab === 'artifacts' ? (
                <Stack spacing={2}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                      <TextField label="Filename" size="small" value={artifactForm.filename} onChange={(event) => setArtifactForm((current) => ({ ...current, filename: event.target.value }))} sx={{ flex: 1 }} />
                      <TextField label="Content Type" size="small" value={artifactForm.content_type} onChange={(event) => setArtifactForm((current) => ({ ...current, content_type: event.target.value }))} sx={{ flex: 1 }} />
                      <TextField label="Bytes" size="small" type="number" value={artifactForm.size_bytes} onChange={(event) => setArtifactForm((current) => ({ ...current, size_bytes: event.target.value }))} sx={{ width: { xs: '100%', md: 140 } }} />
                      <Button variant="outlined" startIcon={<CloudUploadRoundedIcon />} onClick={handleArtifactUploadRequest} disabled={actionState.busy === 'artifact-upload'}>
                        {actionState.busy === 'artifact-upload' ? 'Creating...' : 'Create Upload URL'}
                      </Button>
                    </Stack>
                  </Paper>
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    {artifacts.length ? (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Artifact</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Size</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell align="right">Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {artifacts.map((file) => (
                            <TableRow key={file.file_id}>
                              <TableCell sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                                {file.file_name || file.filename}
                                <Typography variant="caption" color="text.secondary" display="block">{file.content_type || 'application/octet-stream'}</Typography>
                              </TableCell>
                              <TableCell><Chip size="small" color={statusColor(file.upload_request?.status)} label={formatLabel(file.upload_request?.status || 'recorded')} /></TableCell>
                              <TableCell>{formatBytes(file.size_bytes)}</TableCell>
                              <TableCell>{formatValue(file.created_at)}</TableCell>
                              <TableCell align="right">
                                <Button size="small" startIcon={<DownloadRoundedIcon />} onClick={() => handleDownloadArtifact(file)} disabled={Boolean(actionState.busy)}>
                                  Download
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Box sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">No generated artifacts have been recorded for this asset.</Typography></Box>
                    )}
                  </Paper>
                </Stack>
              ) : null}

              {tab === 'documents' ? (
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Documents</Typography>
                    <Button variant="contained" startIcon={<CloudUploadRoundedIcon />} onClick={() => setDocumentDialogOpen(true)}>
                      Upload Document
                    </Button>
                  </Stack>
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    {documents.length ? (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Document</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Version</TableCell>
                            <TableCell>Uploaded</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {documents.map((document) => (
                            <TableRow key={document.asset_document_id}>
                              <TableCell sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                                {document.title}
                                <Typography variant="caption" color="text.secondary" display="block">{document.file?.file_name || document.file_id}</Typography>
                              </TableCell>
                              <TableCell>{document.category === 'other' ? document.category_other : formatLabel(document.category)}</TableCell>
                              <TableCell>{formatValue(document.document_version)}</TableCell>
                              <TableCell>{formatValue(document.uploaded_at)}</TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                  <Tooltip title="Download document">
                                    <span>
                                      <IconButton size="small" onClick={() => handleDocumentDownload(document)} disabled={Boolean(actionState.busy)}>
                                        <DownloadRoundedIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Archive document">
                                    <span>
                                      <IconButton size="small" color="error" onClick={() => handleArchiveDocument(document)} disabled={Boolean(actionState.busy)}>
                                        <DeleteRoundedIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Box sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">No documents have been uploaded for this asset.</Typography></Box>
                    )}
                  </Paper>
                </Stack>
              ) : null}

              {tab === 'capabilities' ? (
                <Stack spacing={2}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                      <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 320 } }}>
                        <InputLabel id="asset-capability-add-label">Capability</InputLabel>
                        <Select
                          labelId="asset-capability-add-label"
                          label="Capability"
                          value={selectedCapabilityId}
                          onChange={(event) => setSelectedCapabilityId(event.target.value)}
                        >
                          <MenuItem value="">Select Capability</MenuItem>
                          {availableUnlinkedCapabilities.map((capability) => (
                            <MenuItem key={capability.capability_definition_id} value={capability.capability_definition_id}>
                              {capability.capability_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button variant="contained" startIcon={<LinkRoundedIcon />} onClick={handleAttachCapability} disabled={!selectedCapabilityId || Boolean(actionState.busy)}>
                        Associate
                      </Button>
                    </Stack>
                  </Paper>
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    {linkedCapabilities.length ? (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Capability</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Need Category</TableCell>
                            <TableCell align="right">Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {linkedCapabilities.map((capability) => (
                            <TableRow key={capability.capability_definition_id}>
                              <TableCell sx={{ fontWeight: 700 }}>
                                <Button component={RouterLink} to={`/capabilities/${capability.capability_definition_id}`} size="small">
                                  {capability.capability_name}
                                </Button>
                              </TableCell>
                              <TableCell><Chip size="small" color={statusColor(capability.status)} label={formatLabel(capability.status || 'active')} /></TableCell>
                              <TableCell>{capability.category_display_name || capability.category_name || 'N/A'}</TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  color="error"
                                  startIcon={<LinkOffRoundedIcon />}
                                  onClick={() => handleDetachCapability(capability)}
                                  disabled={Boolean(actionState.busy)}
                                >
                                  Remove
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Box sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">No capabilities are associated with this asset.</Typography></Box>
                    )}
                  </Paper>
                </Stack>
              ) : null}

              {tab === 'integration' ? (
                <Stack spacing={2}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip label={`Integration: ${formatLabel(asset?.integration_type || 'not_connected')}`} />
                      <Chip label={`Status: ${formatLabel(asset?.integration_status || 'not_configured')}`} color={statusColor(asset?.integration_status)} variant="outlined" />
                      {asset?.integration_gateway_id ? <Chip label={`Gateway: ${asset.integration_gateway_id}`} variant="outlined" /> : null}
                    </Stack>
                  </Paper>
                  <KeyValueTable rows={Object.entries(asset?.integration_metadata || {})} />
                  <KeyValueTable
                    rows={[
                      ['Connector ID', asset?.amx_connector_id],
                      ['Telemetry Topic', asset?.telemetry_topic],
                      ['Actions Topic', asset?.actions_topic],
                      ['Interaction Model ID', asset?.interaction_model_id],
                    ]}
                  />
                </Stack>
              ) : null}

              {tab === 'settings' ? (
                <Stack spacing={2}>
                  {!canManageAsset ? (
                    <Alert severity="info">
                      These settings are read-only because you do not administer this asset&apos;s Business Unit.
                    </Alert>
                  ) : null}

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <SettingsRoundedIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>General Settings</Typography>
                      </Stack>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                          required
                          fullWidth
                          label="Asset Name"
                          value={settingsForm.asset_name}
                          onChange={(event) => updateSettingsForm({ asset_name: event.target.value })}
                          disabled={assetSettingsDisabled}
                        />
                        <TextField
                          fullWidth
                          label="Asset Code"
                          value={settingsForm.asset_code}
                          onChange={(event) => updateSettingsForm({ asset_code: event.target.value })}
                          disabled={assetSettingsDisabled}
                        />
                      </Stack>
                      <TextField
                        multiline
                        minRows={3}
                        label="Description"
                        value={settingsForm.description}
                        onChange={(event) => updateSettingsForm({ description: event.target.value })}
                        disabled={assetSettingsDisabled}
                      />
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <FormControl fullWidth size="small" disabled={assetSettingsDisabled || settingsOptions.loading}>
                          <InputLabel id="asset-settings-type-label">Asset Type</InputLabel>
                          <Select
                            labelId="asset-settings-type-label"
                            label="Asset Type"
                            value={settingsForm.asset_type}
                            onChange={(event) => updateSettingsForm({ asset_type: event.target.value })}
                          >
                            {settingsAssetTypes.map((type) => (
                              <MenuItem key={type.asset_type_code} value={type.asset_type_code}>
                                {type.display_name || formatLabel(type.asset_type_code)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" disabled={assetSettingsDisabled}>
                          <InputLabel id="asset-settings-nature-label">Nature</InputLabel>
                          <Select
                            labelId="asset-settings-nature-label"
                            label="Nature"
                            value={settingsForm.asset_nature}
                            onChange={(event) => updateSettingsForm({ asset_nature: event.target.value })}
                          >
                            <MenuItem value="physical">Physical</MenuItem>
                            <MenuItem value="digital">Digital</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" disabled={assetSettingsDisabled}>
                          <InputLabel id="asset-settings-status-label">Administrative Status</InputLabel>
                          <Select
                            labelId="asset-settings-status-label"
                            label="Administrative Status"
                            value={settingsForm.administrative_status}
                            onChange={(event) => updateSettingsForm({ administrative_status: event.target.value })}
                          >
                            {['draft', 'active', 'inactive', 'retired'].map((status) => (
                              <MenuItem key={status} value={status}>{formatLabel(status)}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                        <FormControlLabel
                          control={(
                            <Switch
                              checked={Boolean(settingsForm.list_on_capabilities_explorer)}
                              onChange={(event) => updateSettingsForm({ list_on_capabilities_explorer: event.target.checked })}
                              disabled={assetSettingsDisabled}
                            />
                          )}
                          label="List on Explorer"
                        />
                        <Typography variant="body2" color="text.secondary">
                          Makes this asset discoverable in the Capabilities Explorer after settings are saved.
                        </Typography>
                      </Paper>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                          fullWidth
                          label="Manufacturer"
                          value={settingsForm.manufacturer}
                          onChange={(event) => updateSettingsForm({ manufacturer: event.target.value })}
                          disabled={assetSettingsDisabled}
                        />
                        <TextField
                          fullWidth
                          label="Model"
                          value={settingsForm.model}
                          onChange={(event) => updateSettingsForm({ model: event.target.value })}
                          disabled={assetSettingsDisabled}
                        />
                        <TextField
                          fullWidth
                          label="Serial Number"
                          value={settingsForm.serial_number}
                          onChange={(event) => updateSettingsForm({ serial_number: event.target.value })}
                          disabled={assetSettingsDisabled}
                        />
                      </Stack>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                        <InventoryImage
                          fileId={settingsForm.asset_image_id}
                          alt={settingsForm.asset_name || 'Asset image'}
                          height={96}
                          sx={{ width: { xs: '100%', md: 160 } }}
                        />
                        {canManageAsset ? (
                          <>
                            <Button
                              component="label"
                              variant="outlined"
                              startIcon={<CloudUploadRoundedIcon />}
                              disabled={assetSettingsDisabled}
                            >
                              {settingsImageUploading ? 'Uploading...' : 'Upload Image'}
                              <input
                                hidden
                                type="file"
                                accept="image/*"
                                onChange={(event) => {
                                  const file = event.target.files?.[0];
                                  event.target.value = '';
                                  if (file) handleSettingsImageUpload(file);
                                }}
                              />
                            </Button>
                            {settingsForm.asset_image_id ? (
                              <Button
                                color="error"
                                onClick={() => updateSettingsForm({ asset_image_id: '' })}
                                disabled={assetSettingsDisabled}
                              >
                                Clear Image
                              </Button>
                            ) : null}
                          </>
                        ) : null}
                      </Stack>
                    </Stack>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>Location & Specifications</Typography>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                          fullWidth
                          label="Location"
                          value={settingsForm.location_label}
                          onChange={(event) => updateSettingsForm({ location_label: event.target.value })}
                          disabled={assetSettingsDisabled}
                        />
                        <TextField
                          fullWidth
                          label="Facility"
                          value={settingsForm.facility_id}
                          onChange={(event) => updateSettingsForm({ facility_id: event.target.value })}
                          disabled={assetSettingsDisabled}
                        />
                      </Stack>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Latitude"
                          value={settingsForm.latitude}
                          onChange={(event) => updateSettingsForm({ latitude: event.target.value })}
                          inputProps={{ min: -90, max: 90, step: 'any' }}
                          disabled={assetSettingsDisabled}
                        />
                        <TextField
                          fullWidth
                          type="number"
                          label="Longitude"
                          value={settingsForm.longitude}
                          onChange={(event) => updateSettingsForm({ longitude: event.target.value })}
                          inputProps={{ min: -180, max: 180, step: 'any' }}
                          disabled={assetSettingsDisabled}
                        />
                      </Stack>
                      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Specification</TableCell>
                              <TableCell>Value</TableCell>
                              <TableCell>Unit</TableCell>
                              <TableCell align="right">Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {settingsSpecificationRows.map((row, index) => (
                              <TableRow key={`asset-setting-spec-${index}`}>
                                <TableCell>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Name"
                                    value={row.label}
                                    onChange={(event) => updateSettingsSpecificationRow(index, { label: event.target.value })}
                                    disabled={assetSettingsDisabled}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Value"
                                    value={row.value}
                                    onChange={(event) => updateSettingsSpecificationRow(index, { value: event.target.value })}
                                    disabled={assetSettingsDisabled}
                                  />
                                </TableCell>
                                <TableCell sx={{ width: 160 }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Unit"
                                    value={row.unit}
                                    onChange={(event) => updateSettingsSpecificationRow(index, { unit: event.target.value })}
                                    disabled={assetSettingsDisabled}
                                  />
                                </TableCell>
                                <TableCell align="right" sx={{ width: 96 }}>
                                  <IconButton
                                    color="error"
                                    onClick={() => removeSettingsSpecificationRow(index)}
                                    disabled={assetSettingsDisabled}
                                    aria-label="Remove specification"
                                  >
                                    <DeleteRoundedIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Paper>
                      {canManageAsset ? (
                        <Button
                          variant="outlined"
                          startIcon={<AddRoundedIcon />}
                          onClick={() => setSettingsSpecificationRows((current) => [
                            ...current,
                            { key: '', label: '', value: '', unit: '' },
                          ])}
                          disabled={assetSettingsDisabled}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          Add Specification
                        </Button>
                      ) : null}
                    </Stack>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>Integration Settings</Typography>
                      <FormControl fullWidth size="small" disabled={assetSettingsDisabled}>
                        <InputLabel id="asset-settings-integration-type-label">Integration</InputLabel>
                        <Select
                          labelId="asset-settings-integration-type-label"
                          label="Integration"
                          value={settingsForm.integration_type}
                          onChange={(event) => updateSettingsForm({ integration_type: event.target.value })}
                        >
                          <MenuItem value="not_connected">Not Connected</MenuItem>
                          <MenuItem value="amx_gateway">AMX Gateway</MenuItem>
                          <MenuItem value="external_integration">External Integration</MenuItem>
                        </Select>
                      </FormControl>
                      {settingsForm.integration_type === 'amx_gateway' ? (
                        <Stack spacing={2}>
                          <TextField
                            label="Gateway ID"
                            value={settingsForm.integration_gateway_id}
                            onChange={(event) => updateSettingsForm({ integration_gateway_id: event.target.value })}
                            disabled={assetSettingsDisabled}
                          />
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Connector ID"
                              value={settingsForm.amx_connector_id}
                              onChange={(event) => updateSettingsForm({ amx_connector_id: event.target.value })}
                              disabled={assetSettingsDisabled}
                            />
                            <TextField
                              fullWidth
                              label="Interaction Model ID"
                              value={settingsForm.interaction_model_id}
                              onChange={(event) => updateSettingsForm({ interaction_model_id: event.target.value })}
                              disabled={assetSettingsDisabled}
                            />
                          </Stack>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="Telemetry Topic"
                              value={settingsForm.telemetry_topic}
                              onChange={(event) => updateSettingsForm({ telemetry_topic: event.target.value })}
                              disabled={assetSettingsDisabled}
                            />
                            <TextField
                              fullWidth
                              label="Actions Topic"
                              value={settingsForm.actions_topic}
                              onChange={(event) => updateSettingsForm({ actions_topic: event.target.value })}
                              disabled={assetSettingsDisabled}
                            />
                          </Stack>
                        </Stack>
                      ) : null}
                      {settingsForm.integration_type === 'external_integration' ? (
                        <Stack spacing={2}>
                          <TextField
                            label="External System"
                            value={settingsForm.external_system}
                            onChange={(event) => updateSettingsForm({ external_system: event.target.value })}
                            disabled={assetSettingsDisabled}
                          />
                          <TextField
                            label="Endpoint"
                            value={settingsForm.external_endpoint}
                            onChange={(event) => updateSettingsForm({ external_endpoint: event.target.value })}
                            disabled={assetSettingsDisabled}
                          />
                        </Stack>
                      ) : null}
                      <TextField
                        multiline
                        minRows={3}
                        label="Integration Notes"
                        value={settingsForm.integration_notes}
                        onChange={(event) => updateSettingsForm({ integration_notes: event.target.value })}
                        disabled={assetSettingsDisabled}
                      />
                    </Stack>
                  </Paper>

                  {canManageAsset ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button
                        variant="contained"
                        startIcon={<SaveRoundedIcon />}
                        onClick={handleSettingsSave}
                        disabled={assetSettingsDisabled}
                      >
                        {actionState.busy === 'asset-settings' ? 'Saving...' : 'Save Settings'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={resetSettingsForm}
                        disabled={assetSettingsDisabled}
                      >
                        Reset Changes
                      </Button>
                    </Stack>
                  ) : null}
                </Stack>
              ) : null}
            </Stack>
          )}
        </Box>
      </Box>

      <Dialog open={documentDialogOpen} onClose={() => !actionState.busy && setDocumentDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Button component="label" variant="outlined" startIcon={<CloudUploadRoundedIcon />}>
              {documentFile ? documentFile.name : 'Select File'}
              <input
                hidden
                type="file"
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  event.target.value = '';
                  setDocumentFile(file);
                  if (file && !documentForm.title) {
                    setDocumentForm((current) => ({ ...current, title: file.name }));
                  }
                }}
              />
            </Button>
            <TextField label="Title" value={documentForm.title} onChange={(event) => setDocumentForm((current) => ({ ...current, title: event.target.value }))} />
            <FormControl fullWidth size="small">
              <InputLabel id="asset-document-category-label">Category</InputLabel>
              <Select
                labelId="asset-document-category-label"
                label="Category"
                value={documentForm.category}
                onChange={(event) => setDocumentForm((current) => ({ ...current, category: event.target.value }))}
              >
                {DOCUMENT_CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>{formatLabel(category)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {documentForm.category === 'other' ? (
              <TextField label="Other Category" value={documentForm.category_other} onChange={(event) => setDocumentForm((current) => ({ ...current, category_other: event.target.value }))} />
            ) : null}
            <TextField multiline minRows={3} label="Description" value={documentForm.description} onChange={(event) => setDocumentForm((current) => ({ ...current, description: event.target.value }))} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth label="Version" value={documentForm.document_version} onChange={(event) => setDocumentForm((current) => ({ ...current, document_version: event.target.value }))} />
              <TextField fullWidth label="Author" value={documentForm.source_author} onChange={(event) => setDocumentForm((current) => ({ ...current, source_author: event.target.value }))} />
            </Stack>
            <TextField type="date" label="Document Date" InputLabelProps={{ shrink: true }} value={documentForm.document_date} onChange={(event) => setDocumentForm((current) => ({ ...current, document_date: event.target.value }))} />
            <TextField label="Tags" value={documentForm.tags} onChange={(event) => setDocumentForm((current) => ({ ...current, tags: event.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocumentDialogOpen(false)} disabled={Boolean(actionState.busy)}>Cancel</Button>
          <Button variant="contained" startIcon={<CloudUploadRoundedIcon />} onClick={handleDocumentUpload} disabled={actionState.busy === 'document-upload'}>
            {actionState.busy === 'document-upload' ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </AppTheme>
  );
}
